import { useState, useEffect } from 'react';
import {
  Calendar,
  ImagePlus,
  Eye,
  Trash2,
  X,
  FileText,
  CheckCircle2,
  ExternalLink,
} from 'lucide-react';

export type DriverLicenseData = {
  licenseNumber: string;
  issueDate: string;
  expiryDate: string;
  driverPhoto: File | string | null;
  drivingLicense: File | string | null;
  passportId: File | string | null;
  vehicleInsurance: File | string | null;
  policeClearance: File | string | null;
  medicalCertificate: File | null;
};

type Step2Props = {
  data: DriverLicenseData;
  updateData: (data: Partial<DriverLicenseData>) => void;
};

type UploadBoxProps = {
  label: string;
  file: File | string | null;
  onFileSelect: (file: File) => void;
  onRemove: () => void;
  onPreview: (file: File | string, label: string) => void;
  accept?: string;
};

const formatFileSize = (bytes: number): string => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const UploadBox = ({
  label,
  file,
  onFileSelect,
  onRemove,
  onPreview,
  accept = 'image/*,.pdf',
}: UploadBoxProps) => {
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (file instanceof File) {
      const url = URL.createObjectURL(file);
      setPreviewUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else if (typeof file === 'string' && file.trim() !== '') {
      setPreviewUrl(file);
    } else {
      setPreviewUrl(null);
    }
  }, [file]);

  const isFileObject = file instanceof File;
  const fileName = isFileObject
    ? file.name
    : typeof file === 'string'
      ? file.split('/').pop() || label
      : '';
  const fileSize = isFileObject ? formatFileSize(file.size) : '';

  const isPdf =
    (isFileObject && file.type === 'application/pdf') ||
    (typeof fileName === 'string' && fileName.toLowerCase().endsWith('.pdf'));

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      onFileSelect(e.dataTransfer.files[0]);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {file && previewUrl ? (
        /* ================= UPLOADED STATE WITH PREVIEW ================= */
        <div className="w-full h-[180px] rounded-[12px] border border-[#BBD495] bg-[#F8FAF5] relative overflow-hidden group shadow-xs flex flex-col items-center justify-center">
          {isPdf ? (
            /* PDF Document Card View */
            <div className="flex flex-col items-center justify-center p-4 gap-2 w-full h-full">
              <div className="w-12 h-12 rounded-full bg-[#FF6A1A]/10 flex items-center justify-center text-[#FF6A1A]">
                <FileText size={28} />
              </div>
              <span
                className="font-host text-[13px] font-semibold text-[#1A1A1A] max-w-[190px] truncate text-center"
                title={fileName}
              >
                {fileName}
              </span>
              {fileSize && (
                <span className="font-host text-[11px] text-[#757575]">
                  {fileSize}
                </span>
              )}
            </div>
          ) : (
            /* Image Preview */
            <img
              src={previewUrl}
              alt={label}
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
            />
          )}

          {/* Top Format Badge */}
          <div className="absolute top-2.5 left-2.5 pointer-events-none">
            <span className="px-2 py-0.5 rounded-[6px] bg-black/60 text-white font-host text-[11px] font-medium backdrop-blur-xs">
              {isPdf ? 'PDF' : fileSize ? fileSize : 'Image'}
            </span>
          </div>

          {/* Hover Overlay with Actions */}
          <div className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex flex-col justify-between p-3 z-10">
            {/* Top action buttons */}
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => onPreview(file, label)}
                className="w-8 h-8 rounded-full bg-white/95 hover:bg-white text-[#1A1A1A] flex items-center justify-center shadow-md transition-transform hover:scale-110 cursor-pointer"
                title="Preview document"
                aria-label="Preview document"
              >
                <Eye size={16} />
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onRemove();
                }}
                className="w-8 h-8 rounded-full bg-white/95 hover:bg-red-50 text-red-600 flex items-center justify-center shadow-md transition-transform hover:scale-110 cursor-pointer"
                title="Remove document"
                aria-label="Remove document"
              >
                <Trash2 size={16} />
              </button>
            </div>

            {/* Center: View Full Document */}
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => onPreview(file, label)}
                className="px-3.5 py-1.5 rounded-full bg-white text-[#1A1A1A] font-host text-[12px] font-bold flex items-center gap-1.5 shadow-md hover:bg-[#F2F2F2] transition-colors cursor-pointer"
              >
                <Eye size={14} className="text-[#FF6A1A]" />
                Preview
              </button>
            </div>

            {/* Bottom info & Change trigger */}
            <div className="flex items-center justify-between w-full text-white text-[11px]">
              <span className="font-host truncate max-w-[130px]" title={fileName}>
                {fileName}
              </span>
              <label className="font-host font-bold underline cursor-pointer hover:text-[#FF6A1A] transition-colors">
                Replace
                <input
                  type="file"
                  className="hidden"
                  accept={accept}
                  onChange={(e) => {
                    if (e.target.files && e.target.files[0]) {
                      onFileSelect(e.target.files[0]);
                    }
                  }}
                />
              </label>
            </div>
          </div>
        </div>
      ) : (
        /* ================= EMPTY UPLOAD DROPZONE ================= */
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          className={`w-full h-[180px] rounded-[12px] border-2 border-dashed transition-all relative flex flex-col items-center justify-center cursor-pointer ${
            isDragging
              ? 'border-[#FF6A1A] bg-[#FFF7F2]'
              : 'border-[#C5D9A5] bg-[#F8FAF5] hover:bg-[#F2F6EC]'
          }`}
        >
          <input
            type="file"
            accept={accept}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                onFileSelect(e.target.files[0]);
              }
            }}
          />
          <div className="flex flex-col items-center gap-2 pointer-events-none">
            <ImagePlus size={32} className="text-[#1A1A1A] mb-2" />
            <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
              Drag & Drop images or
            </span>
            <span className="font-host text-[13px] font-medium text-[#1A1A1A]">
              Browse <span className="text-[#3B5998]">Files</span>
            </span>
          </div>
        </div>
      )}

      {/* Label beneath box */}
      <span className="font-host text-[14px] font-bold text-[#1A1A1A] text-center flex items-center justify-center gap-1.5">
        {label}
        {file && <CheckCircle2 size={16} className="text-[#4CAF50]" />}
      </span>
    </div>
  );
};

export const Step2LicenseDocument = ({ data, updateData }: Step2Props) => {
  const [previewDoc, setPreviewDoc] = useState<{
    file: File | string;
    label: string;
  } | null>(null);

  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!previewDoc) {
      setLightboxUrl(null);
      return;
    }

    if (previewDoc.file instanceof File) {
      const url = URL.createObjectURL(previewDoc.file);
      setLightboxUrl(url);
      return () => {
        URL.revokeObjectURL(url);
      };
    } else if (typeof previewDoc.file === 'string') {
      setLightboxUrl(previewDoc.file);
    }
  }, [previewDoc]);

  // Handle ESC to close preview modal
  useEffect(() => {
    if (!previewDoc) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setPreviewDoc(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [previewDoc]);

  const isLightboxPdf =
    Boolean(previewDoc?.file instanceof File && previewDoc.file.type === 'application/pdf') ||
    (typeof previewDoc?.file === 'string' && previewDoc.file.toLowerCase().endsWith('.pdf'));

  const lightboxFileName =
    previewDoc?.file instanceof File
      ? previewDoc.file.name
      : typeof previewDoc?.file === 'string'
        ? previewDoc.file.split('/').pop() || previewDoc.label
        : '';

  return (
    <div className="flex flex-col gap-8 w-full max-w-[1000px] mx-auto px-[32px]">
      {/* Document Section */}
      <div className="flex flex-col gap-4">
        <h3 className="font-host text-[16px] font-bold text-[#1A1A1A]">Document</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* License Number */}
          <div className="flex flex-col gap-1.5">
            <label className="font-host text-[13px] font-medium text-[#757575]">
              License Number
            </label>
            <input
              type="text"
              placeholder="AT-9876543"
              value={data.licenseNumber}
              onChange={(e) => updateData({ licenseNumber: e.target.value })}
              className="w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] text-[#1A1A1A] placeholder:text-[#9E9E9E] focus:outline-none focus:border-[#FF6A1A]"
            />
          </div>

          {/* Issue Date */}
          <div className="flex flex-col gap-1.5">
            <label className="font-host text-[13px] font-medium text-[#757575]">
              Issue Date
            </label>
            <div className="relative">
              <input
                type="date"
                value={data.issueDate}
                onChange={(e) => updateData({ issueDate: e.target.value })}
                className={`w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] focus:outline-none focus:border-[#FF6A1A] appearance-none ${
                  !data.issueDate ? 'text-[#9E9E9E]' : 'text-[#1A1A1A]'
                }`}
              />
              <Calendar
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1A1A1A] pointer-events-none"
                size={20}
              />
            </div>
          </div>

          {/* Expiry Date */}
          <div className="flex flex-col gap-1.5">
            <label className="font-host text-[13px] font-medium text-[#757575]">
              Expiry Date
            </label>
            <div className="relative">
              <input
                type="date"
                value={data.expiryDate}
                onChange={(e) => updateData({ expiryDate: e.target.value })}
                className={`w-full h-[48px] rounded-[8px] border border-[#E0E0E0] bg-white px-4 font-host text-[14px] focus:outline-none focus:border-[#FF6A1A] appearance-none ${
                  !data.expiryDate ? 'text-[#9E9E9E]' : 'text-[#1A1A1A]'
                }`}
              />
              <Calendar
                className="absolute right-4 top-1/2 -translate-y-1/2 text-[#1A1A1A] pointer-events-none"
                size={20}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Upload Documents Section */}
      <div className="flex flex-col gap-4 mt-2">
        <h3 className="font-host text-[16px] font-bold text-[#1A1A1A]">Upload Documents</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-6 gap-y-10">
          <UploadBox
            label="Driver Photo"
            file={data.driverPhoto}
            onFileSelect={(f) => updateData({ driverPhoto: f })}
            onRemove={() => updateData({ driverPhoto: null })}
            onPreview={(file, label) => setPreviewDoc({ file, label })}
          />
          <UploadBox
            label="Driving License"
            file={data.drivingLicense}
            onFileSelect={(f) => updateData({ drivingLicense: f })}
            onRemove={() => updateData({ drivingLicense: null })}
            onPreview={(file, label) => setPreviewDoc({ file, label })}
          />
          <UploadBox
            label="Passport/ID"
            file={data.passportId}
            onFileSelect={(f) => updateData({ passportId: f })}
            onRemove={() => updateData({ passportId: null })}
            onPreview={(file, label) => setPreviewDoc({ file, label })}
          />
          <UploadBox
            label="Vehicle Insurance"
            file={data.vehicleInsurance}
            onFileSelect={(f) => updateData({ vehicleInsurance: f })}
            onRemove={() => updateData({ vehicleInsurance: null })}
            onPreview={(file, label) => setPreviewDoc({ file, label })}
          />
          <UploadBox
            label="Police Clearance"
            file={data.policeClearance}
            onFileSelect={(f) => updateData({ policeClearance: f })}
            onRemove={() => updateData({ policeClearance: null })}
            onPreview={(file, label) => setPreviewDoc({ file, label })}
          />
          <UploadBox
            label="Medical Certificate"
            file={data.medicalCertificate}
            onFileSelect={(f) => updateData({ medicalCertificate: f })}
            onRemove={() => updateData({ medicalCertificate: null })}
            onPreview={(file, label) => setPreviewDoc({ file, label })}
          />
        </div>
      </div>

      {/* ================= DOCUMENT PREVIEW MODAL / LIGHTBOX ================= */}
      {previewDoc && lightboxUrl && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/75 backdrop-blur-xs p-4 sm:p-6"
          onClick={() => setPreviewDoc(null)}
          role="dialog"
          aria-modal="true"
          aria-label={previewDoc.label}
        >
          <div
            className="relative w-full max-w-[800px] bg-white rounded-[20px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#EEEEEE] bg-[#FAFAFA]">
              <div className="flex flex-col">
                <h4 className="font-host text-[17px] font-bold text-[#1A1A1A]">
                  {previewDoc.label}
                </h4>
                {lightboxFileName && (
                  <span className="font-host text-[12px] text-[#757575] truncate max-w-[400px]">
                    {lightboxFileName}
                  </span>
                )}
              </div>

              <div className="flex items-center gap-2">
                {lightboxUrl && (
                  <a
                    href={lightboxUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="p-2 rounded-full hover:bg-gray-200 text-[#757575] hover:text-[#1A1A1A] transition-colors"
                    title="Open in new tab"
                  >
                    <ExternalLink size={18} />
                  </a>
                )}
                <button
                  type="button"
                  onClick={() => setPreviewDoc(null)}
                  className="p-2 rounded-full hover:bg-gray-200 text-[#757575] hover:text-[#1A1A1A] transition-colors cursor-pointer"
                  title="Close"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="flex-1 p-6 overflow-auto flex items-center justify-center bg-[#F5F5F5] min-h-[300px]">
              {isLightboxPdf ? (
                <iframe
                  src={lightboxUrl}
                  title={previewDoc.label}
                  className="w-full h-[60vh] rounded-[12px] border border-[#E0E0E0] bg-white"
                />
              ) : (
                <img
                  src={lightboxUrl}
                  alt={previewDoc.label}
                  className="max-h-[65vh] max-w-full object-contain rounded-[12px] shadow-md"
                />
              )}
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-between px-6 py-3 border-t border-[#EEEEEE] bg-white">
              <span className="font-host text-[12px] text-[#9E9E9E]">
                Press ESC or click outside to close
              </span>
              <button
                type="button"
                onClick={() => setPreviewDoc(null)}
                className="px-5 py-2 rounded-[20px] bg-[#1A1A1A] text-white font-host text-[13px] font-bold hover:bg-gray-800 transition-colors cursor-pointer"
              >
                Close Preview
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
